import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import { Copy } from 'lucide-react'
import { FC, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import type { PayIdDetails } from '@/wallet/types'
import { WALLET_COPY } from '@/locales/en'

type PayIdDetailsProps = {
    payIdDetails?: PayIdDetails
}

type TabProps = {
    label: string
    value: string
}
const Tab: FC<TabProps> = ({ label, value }) => {
    return (
        <TabsTrigger
            value={value}
            className="data-[state=active]:font-semibold text-foreground w-full py-3 rounded-b-none data-[state=active]:bg-secondary-light"
        >
            {label}
        </TabsTrigger>
    )
}

export default function DepositOptions(props: PayIdDetailsProps) {
    if (!props.payIdDetails) {
        return <p>Bank account details are being loaded. Please try again in a moment.</p>
    }

    const hasPayId = !!props.payIdDetails.payId

    if (!hasPayId) {
        return (
            <div className="mt-4">
                <BankAccountDetailsTab payIdDetails={props.payIdDetails} />
            </div>
        )
    }

    return (
        <Tabs defaultValue="payid" className="mt-4">
            <TabsList className="p-0 h-auto bg-background flex justify-center">
                <Tab label={WALLET_COPY.depositOptions.payIdTabLabel} value="payid" />
                <Tab label={WALLET_COPY.depositOptions.bankAccountTabLabel} value="account" />
            </TabsList>
            <div className="h-[2px] bg-primary-light" />
            <TabsContent tabIndex={-1} className="pt-4" value="payid">
                <PayIDTab payIdDetails={props.payIdDetails} />
            </TabsContent>
            <TabsContent tabIndex={-1} className="pt-4" value="account">
                <BankAccountDetailsTab payIdDetails={props.payIdDetails} />
            </TabsContent>
        </Tabs>
    )
}

function PayIDTab({ payIdDetails }: PayIdDetailsProps) {
    const [isPayIdCopied, setIsPayIdCopied] = useState(false)

    const handlePayIdCopied = () => {
        setIsPayIdCopied(true)
        analytics.track(ANALYTICS_EVENTS.WALLET.PAY_ID_ADDRESS_COPIED)
    }

    if (!payIdDetails?.payId) return <p>{WALLET_COPY.depositOptions.payIdSetupMessage}</p>

    return (
        <div className="px-5">
            <div className="py-3 border-b">
                <label className="font-light">{WALLET_COPY.depositOptions.payIdLabel}</label>
                <p className="flex space-x-3 font-medium">
                    <span>{payIdDetails.payId ?? WALLET_COPY.depositOptions.payIdPlaceholder}</span>{' '}
                    {payIdDetails.payId && (
                        <div className="relative">
                            {isPayIdCopied && (
                                <span className="absolute -top-10 -left-4 text-xs text-lime-700 font-semibold bg-white p-2 rounded border">
                                    {WALLET_COPY.depositOptions.copiedFeedback}
                                </span>
                            )}
                            <CopyToClipboard text={payIdDetails.payId} onCopy={handlePayIdCopied}>
                                <button>
                                    <Copy />
                                </button>
                            </CopyToClipboard>
                        </div>
                    )}
                </p>
            </div>
            <div className="py-3 border-b">
                <label className="font-light">{WALLET_COPY.depositOptions.payIdNameLabel}</label>
                <p className="flex space-x-3 font-medium">{payIdDetails.payIdName}</p>
            </div>
        </div>
    )
}

function BankAccountDetailsTab({ payIdDetails }: PayIdDetailsProps) {
    if (!payIdDetails) return null

    return (
        <div className="px-5">
            <div className="py-3 border-b">
                <label className="font-light">{WALLET_COPY.depositOptions.bankAccountNameLabel}</label>
                <p className="flex space-x-3 font-medium">
                    <span>{payIdDetails.bankAccountName}</span>{' '}
                </p>
            </div>

            <div className="py-3 border-b">
                <label className="font-light">{WALLET_COPY.depositOptions.bankAccountNumberLabel}</label>
                <p className="flex space-x-3 font-medium">{payIdDetails.bankAccountNumber}</p>
            </div>
            <div className="py-3 border-b">
                <label className="font-light">{WALLET_COPY.depositOptions.bsbLabel}</label>
                <p className="flex space-x-3 font-medium">{payIdDetails.bsb}</p>
            </div>
        </div>
    )
}
